import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { OidcConfigurationNameContext } from "../../contexts/OidcConfigurationNameContext";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import IRAGSource from "../../types/interfaces/IRAGSource";
import { getRAGSources } from "../../functions/requests/getRAGSources";
import { RAGSourcesContext } from "../../contexts/RAGSourcesContext";
import { useAuthorizationError } from "../../contexts/AuthorizationErrorContext";

export const RagSourcesProvider = ({ children }: { children: ReactNode }) => {
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
  const { setAuthError } = useAuthorizationError();
  const [ragSources, setRagSources] = useState<IRAGSource[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const reloadSources = useCallback(async () => {
    if (!oidcAccessToken?.accessToken) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ragSourcesResponse = await getRAGSources(oidcAccessToken);
      setRagSources(ragSourcesResponse);
    } catch (err) {
      // Check if this is a 401 unauthorized error
      if (err instanceof Error) {
        const errorMessage = err.message.toLowerCase();
        if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
          console.log("RagSourcesProvider: Setting authorization error");
          // Set the authorization error in context
          setAuthError({
            message: err.message,
            endpoint: '/chat-config/rag_sources',
            provider: oidcConfigurationName,
            timestamp: new Date().toISOString()
          });
          return; // Don't set error state, we're handling this via context
        } else {
          setError(err);
        }
      } else {
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  }, [oidcAccessToken, oidcConfigurationName, setAuthError]);

  useEffect(() => {
    void reloadSources();
  }, [reloadSources]);

  return (
    <RAGSourcesContext.Provider
      value={{
        ragSources: ragSources,
        loading: loading,
        error: error,
        reload: reloadSources,
        name: "rag sources",
      }}
    >
      {children}
    </RAGSourcesContext.Provider>
  );
};
